import {
  Component,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { Event } from '@api/models/event/event';
import { UrlService } from '@utils/services/url/url.service';
import { IMAGE_RESOLUTION } from '@api/models/image/image-resolution';
import { ImageSubType } from '@api/models/image/image-type';
import {
  LiveReporterStatus,
  RegistrationStatus,
} from '@api/models/event/event-team';
import { IUser } from '@api/models/user/user';
import { GameListService } from '@components/games/services/game-list.service';
import {
  MatchPollingService,
  MatchPollUpdateType,
} from '@lib-modules/match/services/match-polling/match-polling.service';
import { Subscription } from 'rxjs';
import { LivestreamStatus } from '@lib/api/src/lib/models/livestream/streaming-info';
import { ColorScheme } from '@lib/models/color-scheme/color-scheme';
import { VideoSubType } from '@lib/api/src/lib/models/video/video';
import { DownloadService } from '@lib/modules/src/download/download.service';
import { formatDate } from '@angular/common';
import { TeamsHttpService } from '@lib/api/src/lib/http/teams/teams-http.service';
import { Download } from '@lib/modules/src/download/models/download';
import { DownloadState } from '@lib/modules/src/download/models/download-state';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';

enum ReporterStatusText {
  TICKER_NOW = 'Jetzt tickern',
  TICKER_AVAILABLE = 'Ticker frei',
  CONFIRMED = 'Du bist Reporter',
  REGISTERED = 'Angemeldet',
  SELECTED = 'Auswahl bestätigen',
}

@Component({
  selector: 'lib-game-list-item',
  templateUrl: './game-list-item.component.html',
  styleUrls: ['./game-list-item.component.scss'],
})
export class GameListItemComponent implements OnInit, OnChanges, OnDestroy {
  @Input() @HostBinding('class.inline') public inline = false;
  @Input() @HostBinding('class.condensed') public condensed = false;
  @Input() @HostBinding('class.large') public large = false;
  @Input() @HostBinding('class.spaced') public spaced = false;
  @Input() public showDownloads = false;
  @Input() public live = false;
  @Input() public lazyLoadImages = true;
  @Input() public showLeagueInfo = false;
  @Input() public teamNameCentered = true;
  @Input() public openLinksInNewTab = true;
  @Input() public useRelativeLinks = false;
  @Input() public highlightOnHover = false;
  @Input() public showDate = true;
  @Input() public showRound = false;
  @Input() public showStatus = false;
  @Input() public showReporterStatus = false;
  @Input() public showImages = true;
  @Input() public useAbbreviations = false;
  @Input() public displayStatusBackground = true;
  @Input() public useLink = true;
  @Input() public highlightGame = false;
  @Input() public infoBackground: string;
  @Input() public matchLinkPath = 'spiele';
  @Input() public event: Event;
  @Input() public user: IUser;
  @Input() public colorScheme = ColorScheme.Default;
  @Input() public hasFooter = false;
  public minute: string;
  public score: string;
  public progress = 0;
  public logoSize = IMAGE_RESOLUTION.LOW;
  public logoType = ImageSubType.LOGO;
  public liveStreamStatus: string;
  public liveStreamStatusColor: string;
  public replayDownloadLink: string;
  public browserDownload = false;
  private poll$: Subscription;

  constructor(
    @Inject(PLATFORM_ID) protected platformId: any,
    public gameListService: GameListService,
    private matchPollingService: MatchPollingService,
    private downloadService: DownloadService,
    private teamsHttp: TeamsHttpService,
    private clipboard: Clipboard,
    private snackbar: MatSnackBar
  ) {}

  public ngOnInit(): void {
    this.setScore();
    this.startPolling();
    this.updateStatus();
    if (this.showDownloads) {
      const replay = this.event.videos_native.find(
        (video) => video.subtype === VideoSubType.Replay
      );
      this.replayDownloadLink = replay.url;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.user || changes.event) {
      this.updateStatus();
    }
  }

  public ngOnDestroy(): void {
    this.matchPollingService.remove(this.event, [MatchPollUpdateType.Status]);
    this.poll$?.unsubscribe();
  }

  public onCopyLink(): void {
    this.clipboard.copy(this.event.short_url);
    this.snackbar.open('Link kopiert', null, { duration: 2500 });
  }

  public get download(): Download | undefined {
    return this.downloadService.downloads.get(this.event._id);
  }

  public get downloadState(): DownloadState | undefined {
    return this.downloadService.downloads.get(this.event._id)?.currentState;
  }

  private startPolling(): void {
    if (!this.live) {
      return;
    }
    const poll = this.matchPollingService.add(
      this.event,
      this.event.teams.home.teamid,
      [MatchPollUpdateType.Status]
    );
    this.poll$ = poll?.updates.subscribe((update) => {
      if (!update) {
        return;
      }
      if (update.type === MatchPollUpdateType.Status) {
        const data = update.data;
        this.minute = data.ui_minute;
        this.setScore(data.teams.home?.score, data.teams.away?.score);
        if (this.event.status !== data.status) {
          this.gameListService.reloadGame(this.event);
        }
        const currentMinute = +this.minute?.split("'")[0];
        if (isNaN(currentMinute)) {
          this.progress = this.minute === 'Halbzeit' ? 50 : 100;
        } else {
          this.progress = (currentMinute / 90) * 100;
        }
      }
    });
  }

  public getGameLink(eventId: string, teamId: string): string {
    if (teamId) {
      if (this.useRelativeLinks) {
        return `/${this.matchLinkPath}/${eventId}/${teamId}`;
      }
      return UrlService.getMatchUrl(eventId, teamId);
    }
    return '#';
  }

  public prepareTeamName(name: string): string {
    return name.split('/').join(' ');
  }

  public setScore(homeScore?: number, awayScore?: number): void {
    homeScore = homeScore || this.event.teams.home.score;
    awayScore = awayScore || this.event.teams.away.score;
    let newScore = `${homeScore === -1 ? '-' : homeScore} : ${awayScore === -1 ? '-' : awayScore}`;
    if (this.event.scores?.ui_scores?.main_score) {
      newScore = this.event.scores.ui_scores.main_score;
    }
    this.score = newScore.replace(/:/g, ' : ');
  }

  public hasBackground(): boolean {
    return (
      !this.event.isPreGame() &&
      !this.event.isCancelledOrAborted() &&
      !this.event.isInAutoMode() &&
      this.displayStatusBackground
    );
  }

  public async onDownload(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    const homeTeam = this.event.teams?.home;
    const awayTeam = this.event.teams?.away;
    const homeTeamName = homeTeam?.name ?? 'Unbekannt';
    const awayTeamName = awayTeam?.name ?? 'Unbekannt';
    const leagueName = this.event.league_info?.league_name ?? 'Unbekannt';
    const roundName = this.event.round?.ui_name ?? 'Unbekannt';
    const date = formatDate(this.event.timestamps.start, 'YYYYMMdd', 'de-de');
    let filename = `${date}_${leagueName}_${roundName}_${homeTeamName}_vs_${awayTeamName}`;
    filename = filename
      .replace(/[^a-zA-Z0-9-_~äöüÄÖÜß]/g, '-')
      .replace(/-+/g, '-');
    filename += '.mp4';
    this.downloadService.downloadFileFromURL(
      this.replayDownloadLink,
      filename,
      this.event._id
    );
  }

  public onCancelDownload(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    const reply = window.confirm(
      'Soll der Download wirklich abgebrochen werden?'
    );
    if (reply) {
      this.downloadService.cancel(this.download.id);
    }
  }

  public getEventStatusString(): string {
    if (this.event.isPostGame()) {
      return 'Endstand';
    }
    if (this.event.isAborted()) {
      return 'abgebrochen';
    }
    if (this.event.isCancelled()) {
      return 'abgesagt';
    }
    return '';
  }

  private updateStatus(): void {
    this.setReporterStatus();
    this.setLiveStreamStatus();
  }

  private setReporterStatus(): void {
    if (!this.showReporterStatus) {
      return;
    }
    const homeTeam = this.event.teams.home;
    const awayTeam = this.event.teams.away;
    const statusHome = homeTeam.fan_moderator?.user_registration_process_status;
    const statusAway = awayTeam.fan_moderator?.user_registration_process_status;

    if (this.event.isPostGameOrEnded() || this.event.isCancelledOrAborted()) {
      return;
    }

    if (
      homeTeam.fan_moderator.status === LiveReporterStatus.DISABLED &&
      awayTeam.fan_moderator.status === LiveReporterStatus.DISABLED &&
      !this.userIsTeamReporter(homeTeam._id, awayTeam._id)
    ) {
      return;
    }

    if (
      statusHome === RegistrationStatus.REGISTERED ||
      statusAway === RegistrationStatus.REGISTERED
    ) {
      this.liveStreamStatus = ReporterStatusText.REGISTERED;
      return;
    }

    if (
      statusHome === RegistrationStatus.SELECTED ||
      statusAway === RegistrationStatus.SELECTED
    ) {
      this.liveStreamStatus = ReporterStatusText.SELECTED;
      return;
    }
    if (
      statusHome === RegistrationStatus.CONFIRMED ||
      statusAway === RegistrationStatus.CONFIRMED
    ) {
      this.liveStreamStatus = ReporterStatusText.CONFIRMED;
      return;
    }

    if (this.userIsReporter(this.event)) {
      this.liveStreamStatus = ReporterStatusText.CONFIRMED;
      return;
    }

    if (this.event.isSportradar() || this.eventHasReporter(this.event)) {
      return;
    }

    if (this.event.isPreGame()) {
      this.liveStreamStatus = ReporterStatusText.TICKER_AVAILABLE;
      return;
    }
    if (this.event.isActive()) {
      this.liveStreamStatus = ReporterStatusText.TICKER_NOW;
      return;
    }
  }

  private userIsTeamReporter(homeTeamId: string, awayTeamId: string): boolean {
    if (!this.user) {
      return false;
    }
    this.user.moderator_of.forEach((moderator) => {
      if (moderator.teamid === homeTeamId || moderator.teamid === awayTeamId) {
        return true;
      }
    });
    return false;
  }

  private userIsReporter(ev: Partial<Event>): boolean {
    let isReporter = false;

    if (!this.user) {
      return isReporter;
    }

    ev.teams.home.moderators.forEach((moderator) => {
      if (this.user._id === moderator.userid) {
        isReporter = true;
        return;
      }
    });
    ev.teams.away.moderators.forEach((moderator) => {
      if (this.user._id === moderator.userid) {
        isReporter = true;
        return;
      }
    });

    if (
      this.user._id === ev.teams.home.fan_moderator.moderatorid ||
      this.user._id === ev.teams.away.fan_moderator.moderatorid
    ) {
      isReporter = true;
    }

    return isReporter;
  }

  private eventHasReporter(ev: Partial<Event>): boolean {
    return (
      ev.teams.home.fan_moderator.moderatorid !== null ||
      ev.teams.away.fan_moderator.moderatorid !== null ||
      new Event(ev)?.hasReporter
    );
  }

  private setLiveStreamStatus(): void {
    const status = this.event.streaming_info?.live_status;

    switch (status) {
      case LivestreamStatus.Live:
      case LivestreamStatus.Upcoming: {
        this.liveStreamStatus = 'LIVESTREAM';
        this.liveStreamStatusColor = '#f44336';
        break;
      }
      case LivestreamStatus.Replay:
      case LivestreamStatus.Ended: {
        this.liveStreamStatus = 'REPLAY';
        this.liveStreamStatusColor = '#517BBB';
        break;
      }
      case LivestreamStatus.Highlights: {
        this.liveStreamStatus = 'VIDEO-HIGHLIGHTS';
        this.liveStreamStatusColor = '#9747FF';
        break;
      }
    }
  }
}
